<script setup lang="ts">
import { computed, onMounted, onUnmounted } from 'vue';
import { RouterView } from 'vue-router';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faPhone } from '@fortawesome/pro-solid-svg-icons';
import { useTenantStore } from '@sera/core';
import TenantLogo from '../components/TenantLogo.vue';

// Public, unauthenticated shell for the embedded schedule widget route
// (/schedule-appointment). Renders only the tenant brand strip and the
// route content full-width — no internal navigation, no card, no footer.
//
// D-28 (updated 2026-05-20): the widget mutates window.google / Maps
// internal state. The unmount reload that the auth-gated version used to
// carry was disruptive UX. This shell avoids the problem structurally:
// the only "leave" affordance is the logo link, which uses a plain <a>
// (full-page navigation) so Maps state resets naturally on the next page.
const tenantStore = useTenantStore();
const tenantPhone = computed<string>(
  () => tenantStore.config?.tenantPhone ?? '',
);

// The embedded widget is light-only — it doesn't honor our html.dark theme,
// so a dark-mode shell around a light widget reads as broken. Force light
// while this shell is mounted; restore on unmount for in-SPA navigation.
// The persisted theme preference in localStorage is not touched.
let restoreDark = false;
onMounted(() => {
  restoreDark = document.documentElement.classList.contains('dark');
  document.documentElement.classList.remove('dark');
});
onUnmounted(() => {
  if (restoreDark) document.documentElement.classList.add('dark');
});
</script>

<template>
  <div class="h-dvh bg-canvas flex flex-col">
    <!-- Branded strip mirrors PublicShell's logged-out header (same
         h-(--nav-height) + px-4 sm:px-6, same stacked "Need assistance?"
         phone block with a faPhone icon) so the embedded widget route reads
         as the same branded chrome as the rest of the public app. -->
    <header
      data-testid="widget-shell-strip"
      class="h-(--nav-height) bg-secondary flex items-center justify-between px-4 sm:px-6 shrink-0"
    >
      <a href="/" class="flex items-center min-w-0">
        <TenantLogo :max-height="36" :max-width="160" fallback="wordmark" />
      </a>
      <div
        v-if="tenantPhone"
        class="flex flex-col items-end leading-tight shrink-0"
      >
        <span class="text-white/80 text-xs">Need assistance?</span>
        <a
          :href="`tel:${tenantPhone}`"
          class="flex items-center gap-2 text-white text-sm hover:underline"
          data-testid="widget-shell-phone"
        >
          <FontAwesomeIcon :icon="faPhone" class="size-4" />
          <span>{{ tenantPhone }}</span>
        </a>
      </div>
    </header>
    <main class="flex-1 flex flex-col w-full min-h-0 overflow-y-auto">
      <RouterView />
    </main>
  </div>
</template>
